import { useCallback, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, FileText, Presentation, Timer } from 'lucide-react';
import { Badge, Button } from '@/components/ui';
import { cn } from '@/lib/utils';
import { Blocks, type Block } from './Blocks';
import { Rich } from './Rich';

export interface Slide {
  nav: string;
  kicker: string;
  title: string;
  lead?: string;
  blocks: Block[];
}

interface SlideDeckProps {
  slides: Slide[];
  onePagerPath: string;
  footer: string;
}

function pad(n: number) {
  return String(n).padStart(2, '0');
}

/** A keyboard- and URL-navigable slide deck: `?slide=N`, arrow keys, Home and End. */
export function SlideDeck({ slides, onePagerPath, footer }: SlideDeckProps) {
  const total = slides.length;
  const [searchParams, setSearchParams] = useSearchParams();

  const parsed = Number.parseInt(searchParams.get('slide') ?? '1', 10);
  const current = Number.isFinite(parsed) ? Math.min(Math.max(parsed, 1), total) : 1;
  const slide = slides[current - 1];

  const goTo = useCallback(
    (next: number) => {
      const clamped = Math.min(Math.max(next, 1), total);
      if (clamped === current) return;
      setSearchParams({ slide: String(clamped) }, { replace: true });
      window.scrollTo({ top: 0 });
    },
    [current, total, setSearchParams]
  );

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      const target = event.target as HTMLElement | null;
      if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;
      if (event.key === 'ArrowRight' || event.key === 'PageDown') goTo(current + 1);
      if (event.key === 'ArrowLeft' || event.key === 'PageUp') goTo(current - 1);
      if (event.key === 'Home') goTo(1);
      if (event.key === 'End') goTo(total);
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [current, total, goTo]);

  return (
    <div className="mx-auto max-w-5xl px-4 pb-10 pt-8 sm:px-6 md:py-12">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex items-center gap-2 border-b border-accent pb-1">
          <Presentation className="h-4 w-4 text-accent-ink" />
          <span className="label text-accent-ink">Pitch deck</span>
          <span className="font-mono text-xs tabular text-ink-3">
            {pad(current)} / {pad(total)}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" onClick={() => goTo(current - 1)} disabled={current === 1} leftIcon={<ChevronLeft className="h-4 w-4" />} aria-label="Previous slide">
            Prev
          </Button>
          <Button variant="secondary" size="sm" onClick={() => goTo(current + 1)} disabled={current === total} rightIcon={<ChevronRight className="h-4 w-4" />} aria-label="Next slide">
            Next
          </Button>
        </div>
      </div>

      <div className="mb-8 flex gap-1" aria-hidden="true">
        {slides.map((s, i) => (
          <button
            key={s.nav}
            type="button"
            onClick={() => goTo(i + 1)}
            title={`${pad(i + 1)} · ${s.nav}`}
            className={cn(
              'h-1 flex-1 transition-colors',
              i + 1 === current ? 'bg-accent' : i + 1 < current ? 'bg-ink/50' : 'bg-line hover:bg-line-strong'
            )}
          />
        ))}
      </div>

      <article className="mb-10">
        <div className="mb-8">
          <div className="mb-3 flex flex-wrap items-center gap-2.5">
            <Badge variant="accent">{pad(current)}</Badge>
            <p className="label">{slide.kicker}</p>
          </div>
          <h1
            className={cn(
              'font-semibold tracking-heading',
              current === 1 ? 'text-hero-mobile md:text-[3rem] md:leading-[1.08]' : 'text-section-mobile md:text-[2.25rem] md:leading-[1.15]'
            )}
          >
            {slide.title}
          </h1>
          {slide.lead && (
            <p className="mt-4 max-w-3xl text-sm leading-relaxed text-ink-2 sm:text-base">
              <Rich text={slide.lead} />
            </p>
          )}
        </div>
        <div className="space-y-4 sm:space-y-5">
          <Blocks blocks={slide.blocks} />
        </div>
      </article>

      <nav className="mb-8 flex flex-wrap gap-2">
        {slides.map((s, i) => (
          <button
            key={s.nav}
            type="button"
            onClick={() => goTo(i + 1)}
            className={cn(
              'rounded-sm border px-2.5 py-1 font-mono text-[11px] transition-colors',
              i + 1 === current ? 'border-accent bg-accent-soft text-accent-ink' : 'border-line text-ink-3 hover:border-line-strong hover:text-ink'
            )}
          >
            <span className="tabular">{pad(i + 1)}</span>
            <span className="ml-1.5 font-sans">{s.nav}</span>
          </button>
        ))}
      </nav>

      <div className="flex flex-col items-start justify-between gap-3 border-t border-ink pt-5 sm:flex-row sm:items-center">
        <p className="flex items-center gap-2 text-xs text-ink-3">
          <Timer className="h-3.5 w-3.5 shrink-0" />
          {footer}
        </p>
        <Link to={onePagerPath} className="inline-flex shrink-0 items-center gap-1.5 text-xs text-accent-ink hover:underline">
          <FileText className="h-3.5 w-3.5" />
          One-pager
        </Link>
      </div>
    </div>
  );
}
